/* ═══════════════════════════════════════════════════════════
   MADAKE CART BUNDLE v13
   ============================================================
   v13 changes (revert of v12's per-line discount display):
   - v12 added a struck-through "original price" next to a
     discounted live price on every row. This caused an
     inconsistency: Dwell's NATIVE rendering (shown whenever no
     +/- sync has run yet, e.g. on a fresh page load with a
     discount code already applied) shows the FULL, undiscounted
     line price by design — Shopify deliberately keeps order-level
     % discounts out of per-line price display, showing savings
     only at the Subtotal/Discount level. Our old paintRow(), which
     only runs AFTER a sync, was overwriting that same price field
     with the DISCOUNTED amount — so the same row could show either
     full or discounted price depending on whether a sync had just
     run, and the v12 strikethrough (always using the full price)
     would end up duplicating whatever number was already showing.
   - Fix: always show the ORIGINAL (undiscounted) line price,
     consistently, whether a sync has run or not. No strikethrough,
     no discounted per-line amount. The actual discount is only
     ever communicated via the Subtotal/Discount/Estimated total
     panel, which Shopify already renders correctly on its own.
   ============================================================
   v11 changes (root-cause fix for the "items vanish after +/-
   click when a discount is active, reappear on refresh" bug):

   CONFIRMED via direct cart.js snapshot comparison across a real
   repro: the backend updates were ALWAYS succeeding (the v10
   retry-on-failure logic in applyChangeWithRetry works correctly
   — every target reached the correct final quantity). The bug was
   never a backend/discount problem. It was a DOM problem:

   When a discount is active and a line item's key rotates mid-
   sequence (Shopify regenerates the key as a side effect of
   discount reallocation), the OLD key disappears from the next
   cart.js fetch. v10's updateCartDOM() saw the old key missing and
   simply REMOVED that row — but nothing ever created a new row for
   the item's NEW key, because we never trigger Dwell's own
   re-render. The row just vanished until a full page load let
   Dwell rebuild the cart markup with current keys.

   Fix: instead of deleting orphaned rows, RE-KEY them in place.
   We already compute a STABLE IDENTITY for every line (variant_id
   + _linked_to/_slot/_hidden_type for linked items, variant_id +
   _session for the parent) when resolving keys before sending
   /cart/change.js calls. We now reuse that same identity to map
   "this row's old key disappeared, but its identity matches THIS
   item in the fresh cart" → update the row's data-key attribute
   and repaint qty/price, instead of removing+never-recreating it.

   Only rows with NO identity match in the fresh cart (genuinely
   removed items) are deleted.

   Everything else (tombstone CSS, retry-on-failure for
   /cart/change.js, reordering, slot-label hiding, etc.) is
   unchanged from v10.
═══════════════════════════════════════════════════════════ */

(function () {
  'use strict';

  const LINKED_TO_PROP = '_linked_to';
  const SESSION_PROP   = '_session';
  const LINKED_ATTR    = 'data-madake-linked';
  const PARENT_ATTR    = 'data-madake-parent';
  const CACHE_LINKED   = 'madake_linked_keys_v2';
  const CACHE_PARENTS  = 'madake_parent_keys_v2';

  let linkedKeysByToken  = new Map();
  let lineKeyToToken     = new Map();
  let isSyncing    = false;
  let isReordering = false;
  let stateRefreshTimer = null;
  let bodyObserver = null;
  const tombstones = new Set();
  let tombstoneStyleEl = null;

  // ─────────────────────────────────────────────────────────
  // CSS
  // ─────────────────────────────────────────────────────────
  function injectCSS() {
    if (document.getElementById('madake-bundle-css')) return;
    const s = document.createElement('style');
    s.id = 'madake-bundle-css';
    s.textContent = `
      /* Hide cart items until initial classify+reorder completes, to avoid
         the flash of unordered items before bundle grouping is applied. */
      body:not(.madake-cart-ready) .cart-items__list,
      body:not(.madake-cart-ready) cart-items-component {
        opacity: 0 !important;
      }
      body.madake-cart-ready .cart-items__list,
      body.madake-cart-ready cart-items-component {
        opacity: 1 !important;
        transition: opacity 0.15s ease;
      }

      body.madake-cart-locked .cart-items__remove,
      body.madake-cart-locked .quantity-selector-wrapper,
      body.madake-cart-locked cart-quantity-selector-component { display:none!important; }

      /* During sync, dim the cart and items so the customer never sees
         Shopify's intermediate per-request totals or the mid-sequence
         reorder jumping around while sequential calls are in flight. */
      body.madake-cart-locked .cart-items__list,
      body.madake-cart-locked cart-items-component {
        opacity: 0.4 !important;
        pointer-events: none !important;
        transition: opacity 0.15s ease;
      }
      body.madake-cart-locked [data-cart-total],
      body.madake-cart-locked .cart-total,
      body.madake-cart-locked .cart__total,
      body.madake-cart-locked .totals__total-value,
      body.madake-cart-locked [ref="cartTotal"],
      body.madake-cart-locked .cart-drawer__total {
        opacity: 0.4 !important;
        transition: opacity 0.15s ease;
      }

      [${LINKED_ATTR}="true"] .quantity-selector-wrapper,
      [${LINKED_ATTR}="true"] cart-quantity-selector-component,
      [${LINKED_ATTR}="true"] quantity-selector-component,
      [${LINKED_ATTR}="true"] .cart-items__quantity,
      [${LINKED_ATTR}="true"] [class*="quantity-selector"],
      [${LINKED_ATTR}="true"] button.quantity-plus,
      [${LINKED_ATTR}="true"] button.quantity-minus,
      [${LINKED_ATTR}="true"] button.cart-items__remove,
      [${LINKED_ATTR}="true"] input[type="number"] { display:none!important; }

      /* Hide "Towel: Towel #N" / "For: ..." — needed for Shopify dedup
         but not useful for customers to see */
      [${LINKED_ATTR}="true"] .cart-items__property[data-madake-slot-row] { display:none!important; }

      /* Floating swatch preview (body-level, immune to transform stacking context) */
      #madake-floating-preview {
        width: 220px;
        background: #fff;
        border: 1px solid #e0e0e0;
        border-radius: 12px;
        padding: 8px;
        box-shadow: 0 8px 24px rgba(0,0,0,0.18);
        pointer-events: none;
      }
      #madake-floating-preview img {
        width: 100%; max-height: 240px;
        object-fit: contain; background: #f6f6f6;
        border-radius: 8px; display: block;
      }
      #madake-floating-preview span {
        display: block; text-align: center;
        margin-top: 6px; font-size: 12px;
        font-weight: 600; color: #163b2e;
      }
      #madake-floating-preview span em { font-style:normal; color:#14b396; }

      .madake-bundle-toast {
        position:fixed; top:24px; left:50%;
        transform:translateX(-50%) translateY(-30px);
        z-index:999999; background:#14b396; color:#fffafa;
        padding:20px 32px 18px; border-radius:10px;
        box-shadow:0 14px 40px rgba(22,59,46,.35);
        font-size:15px; line-height:1.45; max-width:540px;
        text-align:center; opacity:0;
        transition:opacity .3s ease,transform .3s ease;
        pointer-events:auto; border:2px solid #fffafa;
      }
      .madake-bundle-toast.visible { opacity:1; transform:translateX(-50%) translateY(0); }
      .madake-bundle-toast__title { display:block; font-size:17px; font-weight:700; margin-bottom:6px; }
      .madake-bundle-toast__close {
        position:absolute; top:6px; right:10px;
        background:transparent; border:0; color:#fffafa;
        font-size:22px; line-height:1; cursor:pointer; padding:4px 8px; opacity:.75;
      }
      .madake-bundle-toast__close:hover { opacity:1; }

      @media (max-width:767px) {
        [${LINKED_ATTR}="true"] .cart-items__property dt,
        [${LINKED_ATTR}="true"] .cart-items__property dd,
        [${PARENT_ATTR}="true"] .cart-items__property dt,
        [${PARENT_ATTR}="true"] .cart-items__property dd { font-size:10px!important; line-height:1.4!important; }

        /* Tighten vertical rhythm on linked personalization cards —
           these now have fewer fields but still inherit theme-default
           spacing meant for full product cards. */
        [${LINKED_ATTR}="true"] .cart-items__properties {
          margin-top: 2px !important;
          margin-bottom: 2px !important;
        }
        [${LINKED_ATTR}="true"] .cart-items__property {
          margin: 0 !important;
          padding: 1px 0 !important;
        }
        [${LINKED_ATTR}="true"] .cart-items__media,
        [${LINKED_ATTR}="true"] .cart-items__image {
          width: 48px !important;
          height: 48px !important;
        }
      }
    `;
    document.head.appendChild(s);
  }

  function applyTombstoneCSS() {
    if (!tombstoneStyleEl) {
      tombstoneStyleEl = document.createElement('style');
      tombstoneStyleEl.id = 'madake-tombstone-css';
      document.head.appendChild(tombstoneStyleEl);
    }
    if (tombstones.size === 0) { tombstoneStyleEl.textContent = ''; return; }
    const selectors = [...tombstones].map(k => `[data-key="${CSS.escape(k)}"]`).join(',');
    tombstoneStyleEl.textContent = `${selectors}{display:none!important;}`;
  }

  // ─────────────────────────────────────────────────────────
  // Cache
  // ─────────────────────────────────────────────────────────
  function loadCache(key) {
    try { const r = localStorage.getItem(key); return r ? new Set(JSON.parse(r)) : new Set(); }
    catch { return new Set(); }
  }
  function saveCache() {
    try {
      const all = new Set();
      linkedKeysByToken.forEach(v => v.forEach(k => all.add(k)));
      localStorage.setItem(CACHE_LINKED,  JSON.stringify([...all]));
      localStorage.setItem(CACHE_PARENTS, JSON.stringify([...lineKeyToToken.keys()]));
    } catch {}
  }
  function clearCache() {
    try { localStorage.removeItem(CACHE_LINKED); localStorage.removeItem(CACHE_PARENTS); } catch {}
  }

  // ─────────────────────────────────────────────────────────
  // Fetch + classify
  // ─────────────────────────────────────────────────────────
  async function fetchCart() {
    try { const r = await fetch('/cart.js', { cache:'no-store' }); return r.ok ? r.json() : null; }
    catch { return null; }
  }

  function normalizeProps(p) {
    if (!p) return {};
    if (Array.isArray(p)) { const o={}; p.forEach(([k,v])=>o[k]=v); return o; }
    return p;
  }

  function classify(cart) {
    linkedKeysByToken = new Map();
    lineKeyToToken    = new Map();

    const allTokens = new Set();
    cart.items.forEach(item => {
      const props = normalizeProps(item.properties);
      const lt = props[LINKED_TO_PROP];
      if (!lt) return;
      const tok = String(lt);
      allTokens.add(tok);
      if (!linkedKeysByToken.has(tok)) linkedKeysByToken.set(tok, new Set());
      linkedKeysByToken.get(tok).add(item.key);
    });

    cart.items.forEach(item => {
      const props = normalizeProps(item.properties);
      if (props[LINKED_TO_PROP]) return;
      const sv  = props[SESSION_PROP] ? String(props[SESSION_PROP]) : null;
      const pid = String(item.product_id);
      if (sv && allTokens.has(sv))   lineKeyToToken.set(item.key, sv);
      else if (allTokens.has(pid))   lineKeyToToken.set(item.key, pid);
    });

    saveCache();
  }

  // ─────────────────────────────────────────────────────────
  // Row identification
  // ─────────────────────────────────────────────────────────
  function isLinkedRow(row) {
    const key = row?.dataset?.key;
    if (!key) return false;
    for (const keys of linkedKeysByToken.values()) if (keys.has(key)) return true;
    return loadCache(CACHE_LINKED).has(key);
  }

  function isParentRow(row) {
    const key = row?.dataset?.key;
    if (!key) return false;
    if (lineKeyToToken.has(key)) return true;
    if (loadCache(CACHE_PARENTS).has(key)) return true;
    const dts = row.querySelectorAll('.cart-items__properties dt');
    if ([...dts].some(dt => dt.textContent.trim().toLowerCase() === 'personalization name')) return true;
    const dds = row.querySelectorAll('.cart-items__properties dd');
    return [...dds].some(dd => dd.textContent.trim() === 'madake_set_parent');
  }

  // ─────────────────────────────────────────────────────────
  // Apply marks + reorder
  // ─────────────────────────────────────────────────────────
  function applyMarks() {
    document.querySelectorAll('[data-key]').forEach(row => {
      const linked = isLinkedRow(row);
      const parent = !linked && isParentRow(row);

      if (linked) {
        row.setAttribute(LINKED_ATTR, 'true');
        row.removeAttribute(PARENT_ATTR);
        row.querySelectorAll(
          '.quantity-selector-wrapper, cart-quantity-selector-component, button.cart-items__remove'
        ).forEach(el => el.style.setProperty('display','none','important'));
        removeQtyDisplay(row);
        hideSlotLabel(row);
      } else if (parent) {
        row.setAttribute(PARENT_ATTR, 'true');
        row.removeAttribute(LINKED_ATTR);
        removeQtyDisplay(row);
      } else {
        row.removeAttribute(LINKED_ATTR);
        row.removeAttribute(PARENT_ATTR);
        removeQtyDisplay(row);
      }
    });
    reorderCartDOM();
  }

  function hideSlotLabel(row) {
    // Pass 1: hide redundant "Towel #N" / "For: ..." dt/dd property rows
    row.querySelectorAll('.cart-items__property, .cart-items__properties > div').forEach(wrapper => {
      const dt = wrapper.querySelector('dt');
      if (!dt) return;
      const label = dt.textContent.trim().toLowerCase();
      if (label === 'towel' || label === 'for') {
        wrapper.setAttribute('data-madake-slot-row', '1');
        wrapper.style.setProperty('display', 'none', 'important');
      }
    });

    // Pass 2: hide the duplicate trailing price div — structure is
    // <div><span class="visually-hidden">Price</span><span>Rs. 100.00</span></div>
    row.querySelectorAll('div').forEach(div => {
      if (div.children.length !== 2) return;
      const hiddenLabel = div.querySelector('span.visually-hidden');
      if (!hiddenLabel || hiddenLabel.textContent.trim().toLowerCase() !== 'price') return;
      const priceSpan = [...div.children].find(c => c !== hiddenLabel);
      if (priceSpan && /^Rs\.?\s*[\d,]+\.?\d*$/i.test(priceSpan.textContent.trim())) {
        div.style.setProperty('display', 'none', 'important');
      }
    });
  }

  function injectQtyDisplay(row) {
    const input = row.querySelector('input[type="number"]');
    if (!input) return;
    const qty = input.dataset.cartQuantity || input.value || '1';
    let list = row.querySelector('.cart-items__properties');
    if (!list) {
      const wrap = row.querySelector('.cart-items__variants-wrapper');
      if (!wrap) return;
      list = document.createElement('dl');
      list.className = 'cart-items__properties';
      wrap.appendChild(list);
    }
    let entry = list.querySelector('[data-madake-qty-row]');
    if (!entry) {
      entry = document.createElement('div');
      entry.className = 'cart-items__property';
      entry.setAttribute('data-madake-qty-row', '1');
      entry.innerHTML = '<dt>Qty:</dt><dd></dd>';
      list.appendChild(entry);
    }
    entry.querySelector('dd').textContent = qty;
  }

  function removeQtyDisplay(row) { row.querySelector('[data-madake-qty-row]')?.remove(); }

  function reorderCartDOM() {
    document.querySelectorAll('[data-key]').forEach(parentRow => {
      if (!isParentRow(parentRow)) return;
      const token = lineKeyToToken.get(parentRow.dataset.key);
      if (!token) return;
      const linkedKeys = linkedKeysByToken.get(token);
      if (!linkedKeys?.size) return;

      const linkedRows = [...linkedKeys]
        .map(lk => document.querySelector(`[data-key="${lk}"]`))
        .filter(Boolean);
      if (!linkedRows.length) return;

      let cursor = parentRow;
      let correct = true;
      for (const lr of linkedRows) {
        if (cursor.nextElementSibling !== lr) { correct = false; break; }
        cursor = lr;
      }
      if (correct) return;

      isReordering = true;
      bodyObserver.disconnect();
      let after = parentRow;
      linkedRows.forEach(lr => { after.parentNode.insertBefore(lr, after.nextSibling); after = lr; });
      setTimeout(() => {
        bodyObserver.observe(document.body, { childList: true, subtree: true });
        isReordering = false;
      }, 50);
    });
  }

  async function refreshFromCart() {
    const cart = await fetchCart();
    if (!cart) return null;
    classify(cart);
    applyMarks();
    document.body.classList.add('madake-cart-ready');
    return cart;
  }

  // ─────────────────────────────────────────────────────────
  // Identity-based key resolution
  //
  // Line item KEYS are not guaranteed stable across requests when a
  // discount is active on the cart — Shopify can recompute a line's
  // key as a side effect of discount reallocation triggered by an
  // earlier request in the same sequence. Sending a stale key then
  // 400s with "no valid id or line parameter", which silently drops
  // that line from being updated.
  //
  // Fix: identify each target by STABLE identity (variant id + the
  // _linked_to / _slot / _session properties we control), and re-look
  // up its CURRENT key from a freshly fetched cart immediately before
  // every single /cart/change.js call.
  // ─────────────────────────────────────────────────────────
  function findCurrentKey(freshCart, target, token) {
    if (target.isParent) {
      const match = freshCart.items.find(it => {
        const p = normalizeProps(it.properties);
        const sv = p[SESSION_PROP] ? String(p[SESSION_PROP]) : null;
        return it.variant_id === target.variantId && sv === target.sessionVal;
      });
      return match ? match.key : null;
    }
    const match = freshCart.items.find(it => {
      const p = normalizeProps(it.properties);
      const lt = p[LINKED_TO_PROP] ? String(p[LINKED_TO_PROP]) : null;
      const slot = p['_slot'] !== undefined ? String(p['_slot']) : null;
      return it.variant_id === target.variantId
          && lt === token
          && slot === target.slot
          && (p['_hidden_type'] || null) === target.hiddenType;
    });
    return match ? match.key : null;
  }

  // ─────────────────────────────────────────────────────────
  // STABLE IDENTITY for DOM reconciliation (v11)
  //
  // Same notion of "stable identity" as findCurrentKey above, but
  // expressed as a single string key so we can build key->identity
  // and identity->key maps for two different cart snapshots (the
  // cart BEFORE this sync started, and the cart AFTER it finished)
  // and use them to re-key DOM rows whose line-item key rotated
  // mid-sequence, instead of deleting those rows outright.
  // ─────────────────────────────────────────────────────────
  function computeStableId(item) {
    const props = normalizeProps(item.properties);
    const lt = props[LINKED_TO_PROP];
    if (lt) {
      const slot   = props['_slot'] !== undefined ? String(props['_slot']) : '';
      const hidden = props['_hidden_type'] || '';
      return `L|${item.variant_id}|${String(lt)}|${slot}|${hidden}`;
    }
    const sv = props[SESSION_PROP] ? String(props[SESSION_PROP]) : '';
    return `P|${item.variant_id}|${sv}`;
  }

  function buildIdentityIndex(cart) {
    const byKey = new Map(); // key -> stableId
    const byId  = new Map(); // stableId -> key
    cart.items.forEach(it => {
      const id = computeStableId(it);
      byKey.set(it.key, id);
      byId.set(id, it.key);
    });
    return { byKey, byId };
  }

  // ─────────────────────────────────────────────────────────
  // Apply a single /cart/change.js call with retry-on-failure.
  //
  // CONFIRMED via direct cart.js snapshot comparison: a line item's
  // KEY can change on its own — same variant_id, different hash —
  // even with zero further action, while a discount is active on the
  // cart (Shopify reallocates discount amounts across lines, which
  // appears to regenerate the key as a side effect). This means even
  // a key fetched "fresh" immediately before a request can already be
  // stale by the time that request reaches Shopify's server. A single
  // attempt is not reliable; we must re-resolve and retry on failure.
  //
  // CONFIRMED (v11): this retry logic was already working correctly —
  // every target in the traced repro reached its correct final
  // quantity. The visible bug was downstream of this, in DOM
  // reconciliation (see updateCartDOM / paintRow below).
  // ─────────────────────────────────────────────────────────
  async function applyChangeWithRetry(target, token, newQty, maxAttempts = 4) {
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      const freshCart = await fetchCart();
      if (!freshCart) continue;

      const currentKey = findCurrentKey(freshCart, target, token);
      if (!currentKey) {
        console.warn('[Madake Cart] could not re-resolve current key for target (attempt', attempt, ')', target);
        continue;
      }

      try {
        const resp = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: currentKey, quantity: newQty }),
        });
        if (resp.ok) return true;

        const errBody = await resp.json().catch(() => null);
        const isStaleKeyError = errBody && /no valid id or line parameter/i.test(errBody.message || errBody.description || '');
        console.warn('[Madake Cart] change REJECTED for', currentKey, '(attempt', attempt, 'of', maxAttempts, ') status:', resp.status, 'body:', errBody);

        if (!isStaleKeyError) return false; // different error — retrying won't help
        // else: stale key — loop will re-fetch and retry

      } catch (err) {
        console.error('[Madake Cart] change network failure for target (attempt', attempt, ')', target, err);
      }
    }
    console.error('[Madake Cart] gave up after', maxAttempts, 'attempts for target', target);
    return false;
  }

  // ─────────────────────────────────────────────────────────
  // CLICK INTERCEPT
  // ─────────────────────────────────────────────────────────
  document.addEventListener('click', async e => {
    if (isSyncing) {
      if (e.target.closest('.quantity-plus,.quantity-minus,.cart-items__remove')) {
        e.preventDefault(); e.stopImmediatePropagation();
      }
      return;
    }

    const button = e.target.closest('.quantity-plus,.quantity-minus,.cart-items__remove');
    if (!button) return;

    const row = e.target.closest('[data-key]');
    if (!row) return;

    if (!isParentRow(row)) return;

    e.preventDefault();
    e.stopImmediatePropagation();

    isSyncing = true;
    document.body.classList.add('madake-cart-locked');

    try {
      const cart = await fetchCart();
      if (!cart) { isSyncing = false; document.body.classList.remove('madake-cart-locked'); return; }
      classify(cart);

      const key = row.dataset.key;
      const parentItem = cart.items.find(it => it.key === key);
      if (!parentItem) { isSyncing = false; document.body.classList.remove('madake-cart-locked'); return; }

      let newQty;
      if (button.classList.contains('cart-items__remove')) newQty = 0;
      else if (button.classList.contains('quantity-plus'))  newQty = parentItem.quantity + 1;
      else                                                   newQty = Math.max(0, parentItem.quantity - 1);

      if (newQty > 0) {
        const inp = row.querySelector('input[type="number"]');
        if (inp) { inp.value = newQty; inp.dataset.cartQuantity = String(newQty); }
      }

      await syncBundle(key, newQty, parentItem.quantity, cart);

    } catch (err) {
      console.error('[Madake Cart] error:', err);
      isSyncing = false;
      document.body.classList.remove('madake-cart-locked');
    }

  }, true);

  // ─────────────────────────────────────────────────────────
  // SYNC BUNDLE
  // ─────────────────────────────────────────────────────────
  async function syncBundle(parentKey, newQty, oldQty, cart) {
    try {
      const parentItem = cart.items.find(it => it.key === parentKey);
      if (!parentItem) return;

      const props  = normalizeProps(parentItem.properties);
      const sv     = props[SESSION_PROP] ? String(props[SESSION_PROP]) : null;
      const token  = sv || String(parentItem.product_id);

      const linkedItems = cart.items.filter(it =>
        String(normalizeProps(it.properties)[LINKED_TO_PROP] || '') === token
      );

      if (newQty === 0) {
        const allKeys = [...linkedItems.map(it => it.key), parentKey];

        allKeys.forEach(k => tombstones.add(k));
        applyTombstoneCSS();

        const removedPrice = [parentItem,...linkedItems].reduce((s,it)=>s+(it.line_price||0),0);
        const removedCount = parentItem.quantity + linkedItems.reduce((s,it)=>s+it.quantity,0);
        updateCartTotals({
          total_price: Math.max(0, cart.total_price - removedPrice),
          item_count:  Math.max(0, cart.item_count  - removedCount),
        });
        showNotification(0, oldQty, linkedItems);
        clearCache();

        // Same retry-on-failure approach as the quantity-change branch
        // below — deletion is just as exposed to the discount-driven
        // key rotation confirmed via direct cart.js snapshot comparison.
        // Re-keying is NOT needed here: every target is going to qty 0
        // and we explicitly remove all of `allKeys` from the DOM below
        // regardless of what key the server ends up assigning, since
        // those rows are leaving the cart entirely either way.
        const parentTarget = {
          isParent: true,
          variantId: parentItem.variant_id,
          sessionVal: sv,
        };
        const linkedTargets = linkedItems.map(it => {
          const p = normalizeProps(it.properties);
          return {
            variantId: it.variant_id,
            slot: p['_slot'] !== undefined ? String(p['_slot']) : null,
            hiddenType: p['_hidden_type'] || null,
          };
        });
        const deleteTargets = [parentTarget, ...linkedTargets];

        for (const target of deleteTargets) {
          await applyChangeWithRetry(target, token, 0);
        }

        const updated = await fetchCart();

        allKeys.forEach(k => {
          document.querySelectorAll(`[data-key="${k}"]`).forEach(r => r.remove());
          tombstones.delete(k);
        });
        applyTombstoneCSS();

        if (!updated) return;
        classify(updated);
        updateCartTotals(updated);
        if (updated.item_count === 0 && window.location.pathname === '/cart') window.location.reload();

      } else {
        // v11: capture the PRE-CHANGE identity map before we send any
        // /cart/change.js calls. This is what lets us re-key DOM rows
        // afterward instead of deleting+never-recreating them when a
        // line item's key rotates mid-sequence under an active discount.
        const preIdentity = buildIdentityIndex(cart);

        const parentTarget = {
          isParent: true,
          variantId: parentItem.variant_id,
          sessionVal: sv,
        };
        const linkedTargets = linkedItems.map(it => {
          const p = normalizeProps(it.properties);
          return {
            variantId: it.variant_id,
            slot: p['_slot'] !== undefined ? String(p['_slot']) : null,
            hiddenType: p['_hidden_type'] || null,
          };
        });
        const targets = [parentTarget, ...linkedTargets];

        for (const target of targets) {
          await applyChangeWithRetry(target, token, newQty);
        }

        const updated = await fetchCart();
        if (!updated) return;

        classify(updated);
        updateCartDOM(updated, preIdentity);
        applyMarks();
        ['cart:updated','cart:refresh','shopify:cart:updated'].forEach(n =>
          document.dispatchEvent(new CustomEvent(n, { bubbles:true, detail:{ cart:updated } }))
        );
        showNotification(newQty, oldQty, linkedItems);
      }
    } finally {
      isSyncing = false;
      document.body.classList.remove('madake-cart-locked');
    }
  }

  // ─────────────────────────────────────────────────────────
  // DOM + totals
  // ─────────────────────────────────────────────────────────

  // Paints a row's quantity/price display from a cart.js item. Shared
  // by the "key unchanged" and "key re-keyed" paths in updateCartDOM.
  //
  // v13: always uses original_line_price (undiscounted), NOT
  // line_price. This keeps the displayed price consistent with
  // Dwell's own native rendering (which never bakes order-level %
  // discounts into the per-line price by design) regardless of
  // whether a sync has run. The actual discount is only ever shown
  // at the Subtotal/Discount level, not per row.
  function paintRow(row, item) {
    const inp = row.querySelector('input[type="number"]');
    if (inp) { inp.value = item.quantity; inp.dataset.cartQuantity = String(item.quantity); }
    const pe = row.querySelector('.cart-items__price text-component');
    if (pe) {
      const price = typeof item.original_line_price === 'number' ? item.original_line_price : item.line_price;
      const f = fmt(price);
      pe.setAttribute('value',f); pe.textContent=f;
    }
    const qd = row.querySelector('[data-madake-qty-row] dd');
    if (qd) qd.textContent = String(item.quantity);
  }

  // v11: preIdentity (optional) is the { byKey, byId } map computed
  // from buildIdentityIndex() over the cart snapshot taken BEFORE any
  // /cart/change.js calls were sent for this sync. When a row's
  // data-key is no longer present in the fresh `cart`, we use this to
  // check whether that row's stable identity matches a DIFFERENT item
  // in the fresh cart (i.e. its key rotated) — and if so, re-key the
  // row in place instead of removing it.
  function updateCartDOM(cart, preIdentity) {
    const present = new Set(cart.items.map(it=>it.key));
    const newIdentity = preIdentity ? buildIdentityIndex(cart) : null;
    const claimedNewKeys = new Set(); // guards against double re-keying onto the same new key

    document.querySelectorAll('[data-key]').forEach(row => {
      const key = row.dataset.key; if (!key) return;

      if (present.has(key)) {
        const item = cart.items.find(it=>it.key===key);
        if (item) paintRow(row, item);
        return;
      }

      // Key vanished from the fresh cart — try identity-based re-keying
      // before giving up and removing the row.
      if (preIdentity && newIdentity) {
        const stableId = preIdentity.byKey.get(key);
        const newKey   = stableId ? newIdentity.byId.get(stableId) : null;

        if (
          newKey &&
          !claimedNewKeys.has(newKey) &&
          !document.querySelector(`[data-key="${newKey}"]`)
        ) {
          const item = cart.items.find(it => it.key === newKey);
          if (item) {
            row.dataset.key = newKey;
            claimedNewKeys.add(newKey);
            paintRow(row, item);
            return;
          }
        }
      }

      // No replacement identity found — this row is genuinely gone.
      row.remove();
    });

    updateCartTotals(cart);
    if (cart.item_count===0 && window.location.pathname==='/cart') window.location.reload();
  }

  function updateCartTotals(cart) {
    const f = fmt(cart.total_price);
    ['[data-cart-total]','.cart-total','.cart__total','.totals__total-value','[ref="cartTotal"]','.cart-drawer__total']
      .forEach(sel => document.querySelectorAll(sel).forEach(el => {
        const tc = el.querySelector('text-component')||(el.tagName.toLowerCase()==='text-component'?el:null);
        if (tc) { tc.setAttribute('value',f); tc.textContent=f; } else el.textContent=f;
      }));
    ['[data-cart-count]','.cart-count','.cart__count','[ref="cartItemCount"]']
      .forEach(sel => document.querySelectorAll(sel).forEach(el => el.textContent=String(cart.item_count)));
  }

  function fmt(cents) { return `Rs. ${(cents/100).toFixed(2)}`; }

  function showNotification(newQty, oldQty, linkedItems) {
    if (window.location.pathname !== '/cart') return;
    let toast = document.querySelector('.madake-bundle-toast');
    if (!toast) { toast=document.createElement('div'); toast.className='madake-bundle-toast'; document.body.appendChild(toast); }
    const isSet = (linkedItems?.length||0) > 2;
    let title, body;
    if (newQty===0) {
      title = isSet ? 'Set bundle removed' : 'Personalization bundle removed';
      body  = isSet ? 'The set and all personalizations have been removed.' : 'The towel and its embroidery + emojis have been removed.';
    } else if (newQty>oldQty) {
      title = `Quantity updated to ${newQty}`;
      body  = `All linked items have been updated to ${newQty}.`;
    } else {
      title = `Quantity reduced to ${newQty}`;
      body  = 'All linked items have been reduced to match.';
    }
    toast.innerHTML=`<button class="madake-bundle-toast__close" aria-label="Close">×</button><span class="madake-bundle-toast__title">${title}</span><span>${body}</span>`;
    toast.querySelector('.madake-bundle-toast__close').onclick=()=>toast.classList.remove('visible');
    toast.classList.add('visible');
    clearTimeout(toast._hideTimer);
    toast._hideTimer=setTimeout(()=>toast.classList.remove('visible'),5500);
  }

  // ─────────────────────────────────────────────────────────
  // Init
  // ─────────────────────────────────────────────────────────
  injectCSS();

  if (document.readyState==='loading') {
    document.addEventListener('DOMContentLoaded', refreshFromCart);
  } else {
    refreshFromCart();
  }

  function scheduleRefresh() {
    clearTimeout(stateRefreshTimer);
    stateRefreshTimer = setTimeout(()=>{ if(!isSyncing) refreshFromCart(); }, 200);
  }

  bodyObserver = new MutationObserver(mutations => {
    if (isSyncing||isReordering) return;
    const touched = mutations.some(m=>[...m.addedNodes].some(n=>
      n.nodeType===1&&(n.matches?.('[data-key],.cart-items,cart-items-component,cart-drawer-component')||n.querySelector?.('[data-key]'))
    ));
    if (!touched) return;
    applyMarks();
    scheduleRefresh();
  });
  bodyObserver.observe(document.body, { childList:true, subtree:true });

  ['cart:updated','cart:refresh','shopify:cart:updated','dialog:open'].forEach(evt=>
    document.addEventListener(evt, ()=>{ if(!isSyncing){ applyMarks(); scheduleRefresh(); } })
  );

  window.madakeCartBundle = {
    debug: ()=>({
      linkedKeysByToken: Object.fromEntries([...linkedKeysByToken.entries()].map(([k,v])=>[k,[...v]])),
      lineKeyToToken: Object.fromEntries(lineKeyToToken),
      isSyncing,
    }),
    refresh: refreshFromCart,
    forceMark: applyMarks,
  };
})();
